// File: client/src/variants/randomizer/rulebook.js
// Canonical rulebook for Randomizer Chess.

const rulebook = {
  overview:
    'Standard chess punctuated by chaos. Every 3 full turns a random event reshapes the game — the board might rotate, a pawn might promote itself, a meteor might vaporise a piece. The next event is always previewed, so you can plan around it.',
  objective:
    'Checkmate the opponent’s king. The events are the same for both players — adapt faster than your opponent.',
  setup:
    'Standard starting position. White moves first. The first event fires after 3 full turns.',
  turnFlow: [
    'Make one normal legal move.',
    'Every 3 full turns (6 half-moves), the previewed event fires immediately.',
    'A new event is then chosen and previewed at the top of the screen.',
  ],
  mechanics: [
    {
      title: 'Events (one fires every 3 full turns)',
      items: [
        '🔄 Board Rotation — the whole board turns 180°.',
        '✨ Teleport — a random piece jumps to a random empty square.',
        '🎲 Random Promotion — a random pawn promotes to a random piece.',
        '🔀 Piece Swap — two random pieces trade places.',
        '⬇️ Gravity — every piece falls straight down, stacking at the bottom.',
        '☄️ Meteor Strike — a random non-king piece is destroyed.',
        '🐴 Knight Fever — for the next turn, every piece moves like a knight.',
      ],
    },
    {
      title: 'Preview',
      body: 'The next event and a countdown are always shown at the top of the screen, so events are chaotic but never a total surprise.',
    },
  ],
  winConditions: [
    'Win by checkmate.',
    'Win if the opponent resigns or runs out of time.',
    'Stalemate is a draw. (Events can create unusual positions — a sudden stalemate is possible.)',
  ],
  tips: [
    'Watch the preview: don’t leave a valuable piece where the next Meteor or Gravity could wreck your structure.',
    'Board Rotation swaps sides of the board — a winning attack can suddenly be defending.',
    'Kings are never destroyed by events, but they can be moved (rotation, gravity).',
  ],
  knownInteractions: [
    'Rule decision: events NEVER destroy a king. Meteor and Teleport skip kings; Gravity and Rotation may relocate them but never remove them.',
    'Rule decision: events fire every 3 FULL turns (6 half-moves) and apply between turns, after the 6th half-move completes.',
    'Rule decision: Knight Fever applies to the side about to move next, for that one turn only, via the legal-move generator.',
    'Rule decision: an event can incidentally leave a player in check or even deliver checkmate/stalemate; the normal win/draw rules then apply on the following turn.',
    'Rule decision (resume): the event sequence is deterministic from a per-game seed. Resuming a saved game preserves the CURRENT previewed event, but events chosen after the resume may differ from the original session’s tail (the RNG position is not replayed). Fresh games are fully deterministic from their seed.',
    'Fog of War and other hidden-information events from the original brief are NOT included — they require a server-authoritative hidden board that the current client-authoritative model can’t enforce. Documented as out of scope for this version.',
    'AI: the computer plays standard chess and is not event-aware; it will not pre-position against the previewed event. Documented limitation.',
  ],
};

export default rulebook;
